// YERİ: src/app/domains/site/sections/booking-widget.section.ts
// Randevu Widgeti — ziyaretçi hizmet + kaynak/personel + tarih seçer, o günün
// MÜSAİT saatlerini görür ve randevu alır. Veri kaynağı portal'daki sektörel
// randevu modülüyle ORTAK Booking tablosudur (services/resources/slots/reservation).
// config: { title, subtitle, moduleSource (sektör filtresi, ops.) }

import { Component, OnChanges, computed, inject, input, signal } from '@angular/core';
import { TenantWebService, BookingSvc, BookingRes, BookingSlotDto } from '../core/tenant-web.service';
import { SiteConfig, cfg } from './section.model';
import { SiteChromeText } from '../core/site-chrome-text';

@Component({
  selector: 'sec-booking-widget',
  standalone: true,
  template: `
    <section id="booking" class="bg-neutral-50 py-24 dark:bg-neutral-900">
      <div class="mx-auto max-w-3xl px-6 lg:px-8">
        <div class="text-center">
          @if (eyebrow()) { <p class="text-sm font-semibold uppercase tracking-widest" style="color:var(--site-accent)">{{ eyebrow() }}</p> }
          <h2 class="mt-3 font-[family-name:var(--site-head-font)] text-3xl font-bold tracking-tight text-neutral-900 dark:text-white sm:text-4xl">{{ title() }}</h2>
          @if (subtitle()) { <p class="mx-auto mt-4 max-w-xl text-lg text-neutral-600 dark:text-neutral-400">{{ subtitle() }}</p> }
        </div>

        <div class="mt-10 rounded-2xl border border-neutral-200 bg-white p-6 shadow-sm dark:border-neutral-800 dark:bg-neutral-950 sm:p-8">
          @if (sent()) {
            <div class="flex flex-col items-center py-8 text-center">
              <span class="flex size-14 items-center justify-center rounded-full text-white" style="background-color:var(--site-primary)">✓</span>
              <p class="mt-4 text-lg font-semibold text-neutral-900 dark:text-white">{{ metin.t('bk.received') }}</p>
              <p class="mt-1 text-sm text-neutral-500">{{ metin.t('bk.receivedHint') }}</p>
            </div>
          } @else if (resources().length === 0 && loaded()) {
            <p class="py-6 text-center text-sm text-neutral-400">{{ metin.t('bk.unavailable') }}</p>
          } @else {
            <div class="flex flex-col gap-4">
              @if (services().length) {
                <div>
                  <label class="mb-1 block text-xs font-medium text-neutral-500">Hizmet</label>
                  <select class="w-full rounded-lg border border-neutral-300 bg-white px-3 py-2.5 text-sm dark:border-neutral-700 dark:bg-neutral-900" (change)="serviceId.set(+$any($event.target).value || null)">
                    <option value="">{{ metin.t('bk.optional') }}</option>
                    @for (s of services(); track s.id) { <option [value]="s.id">{{ s.name }}@if (s.durationMin) { ({{ s.durationMin }} dk)}@if (s.price) {  · {{ s.price }} {{ s.currency || '₺' }}}</option> }
                  </select>
                </div>
              }
              <div class="grid gap-4 sm:grid-cols-2">
                <div>
                  <label class="mb-1 block text-xs font-medium text-neutral-500">{{ metin.t('bk.resource') }}</label>
                  <select class="w-full rounded-lg border border-neutral-300 bg-white px-3 py-2.5 text-sm dark:border-neutral-700 dark:bg-neutral-900" (change)="setResource(+$any($event.target).value)">
                    @for (r of resources(); track r.id) { <option [value]="r.id" [selected]="r.id === resourceId()">{{ r.name }}</option> }
                  </select>
                </div>
                <div>
                  <label class="mb-1 block text-xs font-medium text-neutral-500">{{ metin.t('bk.date') }}</label>
                  <input type="date" class="w-full rounded-lg border border-neutral-300 bg-white px-3 py-2.5 text-sm dark:border-neutral-700 dark:bg-neutral-900" [value]="date()" [min]="today" (change)="setDate($any($event.target).value)" />
                </div>
              </div>

              <div>
                <label class="mb-1 block text-xs font-medium text-neutral-500">{{ metin.t('bk.slots') }}</label>
                @if (loadingSlots()) {
                  <p class="py-3 text-center text-sm text-neutral-400">{{ metin.t('bk.loadingSlots') }}</p>
                } @else if (slots().length) {
                  <div class="grid grid-cols-3 gap-2 sm:grid-cols-4">
                    @for (sl of slots(); track sl.id) {
                      <button type="button" (click)="selectSlot(sl)"
                        class="rounded-lg border px-2 py-2 text-sm font-medium transition"
                        [class]="selectedSlot()?.id === sl.id ? 'border-primary-600 bg-primary-600 text-white' : 'border-neutral-200 hover:border-primary-400 dark:border-neutral-700'">
                        {{ timeOf(sl.startTime) }}
                      </button>
                    }
                  </div>
                } @else {
                  <p class="py-3 text-center text-sm text-neutral-400">{{ metin.t('bk.noSlots') }}</p>
                }
              </div>

              @if (selectedSlot()) {
                <div class="flex flex-col gap-3 border-t border-neutral-200 pt-4 dark:border-neutral-800">
                  <input class="w-full rounded-lg border border-neutral-300 bg-white px-3 py-2.5 text-sm dark:border-neutral-700 dark:bg-neutral-900" placeholder="Adınız Soyadınız" [value]="name()" (input)="name.set($any($event.target).value)" />
                  <div class="grid gap-3 sm:grid-cols-2">
                    <input class="w-full rounded-lg border border-neutral-300 bg-white px-3 py-2.5 text-sm dark:border-neutral-700 dark:bg-neutral-900" type="email" placeholder="E-posta" [value]="email()" (input)="email.set($any($event.target).value)" />
                    <input class="w-full rounded-lg border border-neutral-300 bg-white px-3 py-2.5 text-sm dark:border-neutral-700 dark:bg-neutral-900" placeholder="Telefon" [value]="phone()" (input)="phone.set($any($event.target).value)" />
                  </div>
                  @if (errorMsg()) { <p class="text-sm text-rose-600 dark:text-rose-400">{{ errorMsg() }}</p> }
                  <button type="button" [disabled]="!canSubmit() || sending()"
                    class="w-full rounded-lg px-6 py-3 text-sm font-semibold text-white transition hover:opacity-90 disabled:opacity-50"
                    style="background-color:var(--site-primary)" (click)="submit()">
                    {{ sending() ? metin.t('sending') : ('Randevu Al — ' + timeOf(selectedSlot()!.startTime)) }}
                  </button>
                </div>
              }
            </div>
          }
        </div>
      </div>
    </section>
  `,
})
export class BookingWidgetSection implements OnChanges {
  /** Ziyaretçiye görünen kabuk metinleri — dil sitenin ayarından. */
  protected metin = inject(SiteChromeText);
  private svc = inject(TenantWebService);
  config = input<SiteConfig>({});
  clientId = input<number>(0);

  protected eyebrow = computed(() => cfg(this.config(), 'eyebrow', 'Randevu'));
  protected title = computed(() => cfg(this.config(), 'title', 'Online Randevu Al'));
  protected subtitle = computed(() => cfg(this.config(), 'subtitle', 'Size uygun tarih ve saati seçin, randevunuzu hemen oluşturun.'));
  private moduleSource = computed(() => cfg<string>(this.config(), 'moduleSource', ''));

  protected readonly today = new Date().toISOString().slice(0, 10);
  protected services = signal<BookingSvc[]>([]);
  protected resources = signal<BookingRes[]>([]);
  protected serviceId = signal<number | null>(null);
  protected resourceId = signal<number>(0);
  protected date = signal<string>(this.today);
  protected slots = signal<BookingSlotDto[]>([]);
  protected selectedSlot = signal<BookingSlotDto | null>(null);
  protected loadingSlots = signal(false);
  protected loaded = signal(false);

  protected name = signal('');
  protected email = signal('');
  protected phone = signal('');
  protected sending = signal(false);
  protected sent = signal(false);
  protected errorMsg = signal('');
  protected canSubmit = computed(() => this.name().trim().length > 0 && (this.email().trim().length > 0 || this.phone().trim().length > 0));

  private fetched = false;

  ngOnChanges() {
    const id = this.clientId();
    if (!id || this.fetched) return;
    this.fetched = true;
    const ms = this.moduleSource() || undefined;
    this.svc.getBookingServices(id, ms).subscribe({ next: s => this.services.set(s), error: () => {} });
    this.svc.getBookingResources(id, ms).subscribe({
      next: r => {
        this.resources.set(r);
        this.loaded.set(true);
        if (r.length) { this.resourceId.set(r[0].id); this.loadSlots(); }
      },
      error: () => this.loaded.set(true),
    });
  }

  protected timeOf(iso: string) { return (iso || '').substring(11, 16); }

  protected setResource(id: number) { this.resourceId.set(id); this.selectedSlot.set(null); this.loadSlots(); }
  protected setDate(d: string) { this.date.set(d); this.selectedSlot.set(null); this.loadSlots(); }
  protected selectSlot(sl: BookingSlotDto) { this.selectedSlot.set(sl); }

  private loadSlots() {
    const id = this.clientId();
    if (!id || !this.resourceId()) return;
    this.loadingSlots.set(true);
    this.slots.set([]);
    this.svc.getBookingSlots(id, this.resourceId(), this.date()).subscribe({
      next: s => { this.slots.set(s.filter(x => x.availableCount > 0)); this.loadingSlots.set(false); },
      error: () => this.loadingSlots.set(false),
    });
  }

  protected submit() {
    const id = this.clientId();
    const sl = this.selectedSlot();
    if (!id || !sl || !this.canSubmit()) return;
    this.sending.set(true);
    this.errorMsg.set('');
    this.svc.postBooking(id, {
      resourceId: this.resourceId(), serviceId: this.serviceId(),
      guestName: this.name(), guestEmail: this.email(), guestPhone: this.phone(),
      startTime: sl.startTime, endTime: sl.endTime,
    }).subscribe({
      next: () => { this.sending.set(false); this.sent.set(true); },
      error: (err) => { this.sending.set(false); this.errorMsg.set(err?.error?.message || this.metin.t('bk.failed')); },
    });
  }
}
